import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils';
import { CloudMark } from './CloudMark';
import {
  borderGrey,
  primaryText,
  placeholderPrimary,
  secondaryText,
} from '../../../../styling/stylingHelper';

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  /** Sends the chip text directly (chips bypass the textarea round-trip). */
  onChipClick: (chip: string) => void;
  /** False while a turn streams, with no scope, or with an empty box — greys the send button. */
  canSend: boolean;
  /** A question could be sent right now if there were text (scope picked, nothing streaming). */
  canAsk: boolean;
  isStreaming: boolean;
  /** Chips only make sense on an empty transcript. */
  showSuggestions: boolean;
}

/**
 * The composer: pinned to the bottom of the admin content column so the transcript scrolls
 * under it. Enter sends, Shift+Enter adds a newline. The cloud shape is the page's brand mark.
 */
export const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  onChipClick,
  canSend,
  canAsk,
  isStreaming,
  showSuggestions,
}) => {
  const { t } = useTranslation();

  const suggestionChips = useMemo(
    () => [
      t('cloudAgent.suggestions.margin'),
      t('cloudAgent.suggestions.push'),
      t('cloudAgent.suggestions.supplier'),
      t('cloudAgent.suggestions.waste'),
    ],
    [t]
  );

  return (
    // pb clears the iOS/Android browser chrome: without the safe-area inset the send button sits
    // under the home indicator on a phone.
    <div
      className="sticky bottom-0 z-10 pt-3 pb-2 bg-gray-50 dark:bg-gray-900"
      style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
    >
      {showSuggestions && (
        <div className="flex flex-wrap justify-center gap-2 mb-3">
          {suggestionChips.map((chip, i) => (
            <motion.button
              key={chip}
              type="button"
              onClick={() => onChipClick(chip)}
              disabled={!canAsk}
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 3.2, repeat: Infinity, delay: i * 0.35, ease: 'easeInOut' }}
              whileHover={{ scale: 1.04 }}
              className={cn(
                'rounded-full bg-background dark:bg-background-dark px-4 py-2 text-xs border shadow-sm',
                borderGrey,
                secondaryText,
                'hover:text-purple-600 dark:hover:text-purple-300 disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {chip}
            </motion.button>
          ))}
        </div>
      )}

      <div
        className={cn(
          'relative rounded-[28px] p-2 pl-4 flex items-end gap-2',
          'bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/40 dark:to-purple-950/40',
          'border border-white/60 dark:border-white/5 shadow-sm'
        )}
      >
        {/* The real brand mark rather than a generic cloud glyph. */}
        <CloudMark size={24} className="mb-2.5 max-sm:hidden" />
        <textarea
          className={cn(
            'flex-1 bg-transparent dark:bg-transparent border-0 resize-none py-2.5 text-sm',
            primaryText,
            placeholderPrimary,
            'focus:outline-none focus:ring-0'
          )}
          rows={1}
          placeholder={t('cloudAgent.questionPlaceholder')}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
        />
        <button
          type="button"
          onClick={onSend}
          disabled={!canSend}
          aria-label={t('cloudAgent.ask')}
          className={cn(
            'flex items-center justify-center w-10 h-10 rounded-full text-white dark:text-white transition shrink-0',
            canSend
              ? 'bg-gradient-to-br from-purple-500 to-blue-500 dark:from-purple-500 dark:to-blue-500 hover:opacity-90 shadow'
              : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
          )}
        >
          {isStreaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

export default ChatInput;
